import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import { SITE } from "../site.config";
import useDocumentMeta from "../hooks/useDocumentMeta";

const SITE_URL = "https://powerzautoelectrical.com.au";
const ABN = "49 680 967 088";
const UPDATED = "19 June 2026";

const SECTIONS = [
  {
    title: "Who we are",
    paragraphs: [
      `This Privacy Policy covers ${SITE_URL} (the "website"), operated by Powerz Auto Electrical (ABN ${ABN}), an auto electrical workshop at ${SITE.business.address.full}. We're bound by the Australian Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs), and this page explains in plain English what information we collect when you use the website, why, and what we do with it.`,
    ],
  },
  {
    title: "Information you give us",
    paragraphs: [
      "The website doesn't have user accounts and doesn't store your details in a database. You give us personal information only when you choose to contact us:",
    ],
    list: [
      `Get a Quote form — the form collects your first and last name, phone number and/or email address, vehicle details and a description of the job. When you hit send, the form opens your own email app with that information pre-filled, addressed to ${SITE.business.email}. Nothing is submitted to or stored on the website itself; the email is sent from your device, and we receive it in our business inbox.`,
      "Phone calls — if you call us from a link on the website, your phone number is visible to us the same as any normal call.",
      "Direct email — if you email us, we receive whatever you choose to include.",
      "Reviews — reviews you leave on Google are governed by Google's terms and are publicly visible on Google. We display a selection of them on this website.",
    ],
  },
  {
    title: "How we use it",
    list: [
      "To respond to your enquiry, provide a quote and book your vehicle into the workshop.",
      "To carry out and invoice the work you've authorised.",
      "To contact you about your job — for example to approve additional work or let you know your vehicle is ready.",
      "To keep workshop records (job cards, invoices and warranty records) as required by Australian tax and consumer law.",
    ],
    outro: ["We don't sell, rent or trade your personal information, and we don't send marketing emails or SMS unless you've asked us to."],
  },
  {
    title: "Analytics, tags and advertising cookies",
    paragraphs: [
      "Like most websites, we use Google tools to understand how the website is used and to measure advertising. These tools set cookies and similar identifiers in your browser and collect information such as the pages you visit, how you arrived at the site, your approximate location (city level), device and browser type, and your interactions with the site (for example tapping the Call button or opening the quote form). This data is aggregated and does not include your name or contact details unless you provide them.",
      "Tools currently in use on this website:",
    ],
    list: [
      "Google Analytics 4 (measurement IDs G-MMZRWCBCSV and G-SGRHSW9Z7V) — website traffic and behaviour analytics.",
      "Google Tag Manager (containers GTM-PKRNCMRM and GTM-5PS2JH6W) — used to load and manage the analytics and advertising tags above and below.",
      "Google Ads conversion tracking and remarketing — used to measure whether our Google Ads bring genuine enquiries, and to show relevant Powerz ads to people who have visited the website. Google may use advertising cookies (including the Google Ads cookie and DoubleClick cookie) for this purpose.",
      "Google Fonts and Google Maps links — fonts are served by Google; map links open Google Maps.",
    ],
  },
  {
    title: "Who else sees the data",
    list: [
      "Google LLC — processes the analytics and advertising data described above under Google's Privacy Policy (policies.google.com/privacy). Google may store this data on servers outside Australia, including in the United States.",
      "Localsearch (Localsearch Operations Pty Ltd) — our digital marketing agency, which manages our Google Ads campaigns and has administrator access to our Google Analytics and Google Tag Manager accounts for that purpose. Localsearch sees aggregated analytics and campaign data, not your quote emails.",
      "Our accounting, invoicing and email providers — only to the extent needed to run the workshop and comply with the law.",
    ],
    outro: ["We don't share your personal information with anyone else unless you ask us to or the law requires it."],
  },
  {
    title: "Your choices — opting out",
    list: [
      "Block or delete cookies in your browser settings. The website still works without cookies; the quote form and phone links don't depend on them.",
      "Opt out of Google Analytics across all websites with Google's browser add-on: tools.google.com/dlpage/gaoptout.",
      "Control the ads Google shows you, including turning off ad personalisation, at adssettings.google.com.",
      "Use your browser's private/incognito mode, which clears cookies when you close it.",
    ],
  },
  {
    title: "Storage and security",
    paragraphs: [
      "Quote and job emails are kept in our business email and workshop records for as long as we need them to service your vehicle, honour our 12-month workmanship warranty and meet our record-keeping obligations (generally seven years for financial records). We take reasonable steps to protect that information from misuse, loss and unauthorised access. The website itself is served over HTTPS.",
    ],
  },
  {
    title: "Access, correction and complaints",
    paragraphs: [
      `You can ask us what personal information we hold about you, and ask us to correct or delete it, by emailing ${SITE.business.email} or calling ${SITE.business.phone}. We'll respond within a reasonable time. If you're not happy with how we've handled your information, you can complain to the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.`,
    ],
  },
  {
    title: "Changes to this policy",
    paragraphs: [
      `We may update this page from time to time, for example when we add or remove a tracking tool. The date at the top shows when it was last changed. This policy was last updated on ${UPDATED}.`,
    ],
  },
];

export default function PrivacyPage() {
  const canonical = `${SITE_URL}/privacy`;
  useDocumentMeta({
    title: "Privacy Policy | Powerz Auto Electrical Sunshine Coast",
    description:
      "How Powerz Auto Electrical (Kunda Park, Sunshine Coast) collects and uses information from the Get a Quote form, phone calls, Google Analytics, Google Tag Manager and Google Ads tracking — and how to opt out.",
    canonical,
    image: SITE.business.logo,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Privacy Policy", item: canonical },
        ],
      },
    ],
  });

  return (
    <div className="bg-black text-white">
      <Header />
      <section className="relative bg-black pt-40 md:pt-48 pb-12 md:pb-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-10">
            <ArrowLeft className="w-4 h-4" /> HOME
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">POWERZ AUTO ELECTRICAL</span>
          </div>
          <h1 data-testid="privacy-headline" className="bebas text-white text-5xl md:text-7xl leading-[0.9] tracking-tight max-w-4xl">
            PRIVACY <span className="text-cyan-pz glow-cyan">POLICY.</span>
          </h1>
          <p className="mt-6 text-white/70 barlow text-base md:text-lg max-w-3xl">
            What we collect when you use this website, why, and how to opt out — in plain English. Last updated {UPDATED}.
          </p>
        </div>
      </section>

      <section className="relative bg-black pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 space-y-6">
          {SECTIONS.map((sec, idx) => (
            <article key={sec.title} data-testid={`privacy-section-${idx + 1}`} className="border border-white/10 bg-[#0A0A0A] p-6 md:p-10">
              <div className="flex items-start gap-5">
                <div className="hidden sm:flex w-12 h-12 shrink-0 items-center justify-center border border-cyan-pz/40 bg-cyan-pz/5">
                  <ShieldCheck className="w-5 h-5 text-cyan-pz" />
                </div>
                <div className="flex-1">
                  <p className="bebas text-cyan-pz tracking-[0.3em] text-xs mb-1">SECTION {idx + 1}</p>
                  <h2 className="bebas text-white text-3xl md:text-4xl leading-tight mb-4">{sec.title}</h2>
                  {sec.paragraphs?.map((p, i) => (
                    <p key={i} className="text-white/75 barlow text-base leading-relaxed mb-3">{p}</p>
                  ))}
                  {sec.list && (
                    <ul className="space-y-2 mb-3">
                      {sec.list.map((li, i) => (
                        <li key={i} className="flex gap-3 text-white/75 barlow text-base leading-relaxed">
                          <span className="mt-2 w-1.5 h-1.5 bg-cyan-pz shrink-0" /> <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {sec.outro?.map((p, i) => (
                    <p key={`o${i}`} className="text-white/75 barlow text-base leading-relaxed mb-3">{p}</p>
                  ))}
                </div>
              </div>
            </article>
          ))}
          <p className="text-white/40 barlow text-xs pt-4">
            See also our <Link to="/policies" className="text-cyan-pz hover:underline">Workshop Policies &amp; Liability Disclaimer</Link>.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
